import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { Flex, Box, InlineBanner, Spinner } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { throttle } from 'lodash';
import isElectron from 'is-electron';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { EsriMap } from '../../esri/components/esri-map';
import { areLatLngsEqual, isValidLatLng } from '../../location/helpers';
import {
    getEventLocation,
    getMapViewportUnitLocation,
    getUnitUserLocation,
} from '../../esri/helpers/locations';
import {
    EsriMapContextMenuEntityTypeEnum,
    DeprecatedEsriMapFilterState,
    EsriMapFilterState,
    MapOnChangeParams,
    LayerId,
    WebMap,
} from '../../esri/types';
import { usePhoneCallsStore } from '../../phone-calls/use-phone-calls';
import { useEsriMapBootstrap } from '../hooks/use-esri-map-bootstrap';
import { useEsriMapStore } from '../hooks/use-esri-map-store';
import { useEsriMapViewRef } from '../hooks/use-esri-map-view-ref';
import { MapLedger } from './map-ledger';
import { MapTopMenuBar } from './map-top-menu-bar';
import type { PhoneCallView } from '../../phone-calls/types';
import { EsriMapEntityTypeEnum } from '../../esri/types';
import { ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL } from '../../esri/configurations';
import { convertPhoneCallToGeoPointEntity } from '../../esri/helpers/phone-calls';
import { useInaccurateLocationCheckInterval } from '../hooks/use-inaccurate-location-check-interval';
import {
    MAP_ON_CHANGE_THROTTLE_THRESHOLD,
    CAD_MAP_FILTERS_LOCAL_STORAGE_KEY,
    FR_MAP_FILTERS_LOCAL_STORAGE_KEY,
    MAP_LAYERS_LOCAL_STORAGE_KEY,
} from '../configuration';
import { storageHelpers } from '../../common/helpers/browser-storage';
import {
    SUMMARY_FILTER_CATEGORIES,
    getPrimarySubdivisionOptions,
    getPrimarySubdivisionNameKeyByAgencyType,
    PrimarySubdivisionNames,
} from './helpers';
import { alphabeticalStringComparator } from '../../common/sorting/comparators';
import {
    AgencyTypeGlobal,
    agencyTypeIdToDisplay as agencyTypeIdToDisplayByAgencyTypeGlobal,
    globalAttributes,
} from '../../common/global-attributes';
import { EventContextMenu } from '../../event-context-menu/event-context-menu';
import { UnitContextMenu } from '../../unit-context-menu/unit-context-menu';
import { mapTestHelpers } from './esri-map-test-helpers';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';
import { useEsriMapHandleViewLocationOnMapAction } from '../hooks/use-esri-map-handle-view-location-on-map-action';
import { useResetWorkspaceMapView } from '../hooks/use-reset-workspace-map-view';
import { usePhoneNumberIdsForEvent } from '../hooks/use-phone-number-ids-for-event';
import { useSyncPhoneCallsForSelectedEventInFr } from '../hooks/use-sync-phone-calls-for-selected-event-in-fr';
import {
    ESRI_MAP_FULL_LOAD_DELAY_INITIAL_MS,
    ESRI_MAP_FULL_LOAD_DELAY_REOPEN_MS,
    FEATURE_LAYERS_PROPERTIES,
} from '../../esri/constants';
import { moveLayerOnTop } from '../../esri/helpers/utils';
import { useWorkspaceStore } from '../../panels/use-workspace-store';
import {
    initializeMapFilterStateFromWorkspaceMapDto,
    initializeMapLayerIdsFromWorkspaceMapDto,
    covertMapFiltersToWorkspaceMapFilters,
    convertMapLayerIdsToWorkspaceMapLayers,
} from './helpers';
import { GeoPoint } from '../../location/types';
import { shouldLoadAlternativeBasemap } from '../../esri/base-maps/helpers';
import { useThemeStore } from '~/theme/use-theme-store';

type EsriMapWindowProps = {
    isMapInactive?: boolean;
    mapLastInactiveTimestamp?: number;
    setMapLastInactiveTimestamp?: Dispatch<SetStateAction<number>>;
};

export const EsriMapWindow = observer(function EsriMapWindow({
    isMapInactive,
    mapLastInactiveTimestamp,
    setMapLastInactiveTimestamp,
}: EsriMapWindowProps): React.ReactElement {
    const {
        agencyProfileOptions,
        esriWebmapPortalId,
        mapClusterRadiusInPixels,
        manageDispatchAreasView,
        subdivisionOptionsBootstrapResponse,
        cadDepartmentAgencyView,
        primaryPoliceSubdivisionName,
        primaryFireSubdivisionName,
        primaryEmsSubdivisionName,
        userProfileView,
        userAgencyTypeGlobalAttrId,
    } = useBootstrapState();
    const {
        mapTestsEnabled,
        esriFeatureReductionClusteringEnabled,
        enableUnitClusteringOnMap,
        showMobileDevicesOnMap,
        showUnitLocationWhenGpsIsNotAvailable,
        multiAgencySubdivisions,
        dispatchFromMap,
        createEventAtLocationPin,
        hospitalsEnabled,
        rollOutPersistWorkspaceMapFiltersEnabled,
        rollOutWorkspaceMapCenterpointEnabled,
    } = useEnabledFeatures();
    const { theme } = useThemeStore();
    const mapStore = useEsriMapStore();
    const workspaceStore = useWorkspaceStore();
    const resetWorkspaceMapView = useResetWorkspaceMapView();
    const mapWorkspaceWindow = workspaceStore.getWorkspaceWindowsByWindowType('MAP_V2')?.[0];
    const workspaceMap = mapWorkspaceWindow
        ? workspaceStore.getWorkspaceWindowById(mapWorkspaceWindow.id)?.getWorkspaceMap()
        : undefined;
    const workspaceMapDto = workspaceMap?.getDto();
    const { centerLatitude, centerLongitude, zoomLevel } = workspaceMapDto ?? {};

    const useSavedMapCenterAndZoomLevel =
        rollOutPersistWorkspaceMapFiltersEnabled && rollOutWorkspaceMapCenterpointEnabled;

    const savedMapCenter = useMemo(() => {
        if (!useSavedMapCenterAndZoomLevel) {
            return undefined;
        }
        return isValidLatLng(centerLatitude, centerLongitude)
            ? ({ latitude: centerLatitude, longitude: centerLongitude } as GeoPoint)
            : undefined;
    }, [centerLatitude, centerLongitude, useSavedMapCenterAndZoomLevel]);

    const savedMapZoomLevel = useSavedMapCenterAndZoomLevel ? zoomLevel : undefined;

    const mapViewRef = useEsriMapViewRef();
    if (mapTestsEnabled) {
        // @ts-expect-error
        window.mapStore = mapStore;
        // @ts-expect-error
        window.mapViewRef = mapViewRef;
        // @ts-expect-error
        window.mapTestHelpers = mapTestHelpers;
    }
    const phoneCallsStore = usePhoneCallsStore();
    const mapBootstrapQuery = useEsriMapBootstrap(mapStore);

    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();
    const esriMapHandleViewLocationOnMapAction = useEsriMapHandleViewLocationOnMapAction();
    useSyncPhoneCallsForSelectedEventInFr();

    let esriMapLoadTimeoutId: NodeJS.Timeout;
    const esriMapDelayedActionsTimeoutIds: NodeJS.Timeout[] = [];

    const { agencyTypeGlobalAttrId, agencyId } = cadDepartmentAgencyView;
    const { userId } = userProfileView;

    const isDefaultCenter = !mapStore.mapCenterForUpdate;
    const shouldResetMapOnInitialLoad =
        isDefaultCenter && (!unitAssignment || !mapStore.followingUnitId);

    const selectedUnitLocation = mapStore.selectedUnitId
        ? getMapViewportUnitLocation(
              mapStore.getMapViewportUnit(mapStore.selectedUnitId),
              showUnitLocationWhenGpsIsNotAvailable
          )
        : undefined;
    const selectedEventLocation = mapStore.selectedEventId
        ? getEventLocation(mapStore.getMapViewportEvent(mapStore.selectedEventId))
        : undefined;

    const setDeprecatedMapFilterState = useCallback(
        (filterState: DeprecatedEsriMapFilterState, saveToLocalStorage = true) => {
            mapStore.setDeprecatedMapFilterState(filterState);
            mapStore.updateMapView();
            if (saveToLocalStorage) {
                const key = `${userId}:${
                    isFirstResponder
                        ? FR_MAP_FILTERS_LOCAL_STORAGE_KEY
                        : CAD_MAP_FILTERS_LOCAL_STORAGE_KEY
                }`;
                storageHelpers.setItem(key, filterState);
            }
        },
        [mapStore, userId, isFirstResponder]
    );
    const setFilterState = useCallback(
        (filterState: EsriMapFilterState) => {
            mapStore.setMapFilterState(filterState);
            mapStore.updateMapView();
        },
        [mapStore]
    );

    const setLayerIds = useCallback(
        (layerIds: LayerId[], saveToLocalStorage = true) => {
            mapStore.setVisibleLayersIds(layerIds);
            if (!rollOutPersistWorkspaceMapFiltersEnabled) {
                if (saveToLocalStorage) {
                    const key = `${userId}:${MAP_LAYERS_LOCAL_STORAGE_KEY}`;
                    storageHelpers.setItem(key, layerIds);
                }
            }
        },
        [mapStore, rollOutPersistWorkspaceMapFiltersEnabled, userId]
    );

    const saveMapFilters = useCallback(() => {
        if (rollOutPersistWorkspaceMapFiltersEnabled && workspaceMap) {
            const filtersForWorkspaceDto = covertMapFiltersToWorkspaceMapFilters(
                mapStore.mapFilterState,
                mapStore.mapFilterOptions
            );
            workspaceMap.updateMapFilters(filtersForWorkspaceDto);
        }
    }, [
        rollOutPersistWorkspaceMapFiltersEnabled,
        workspaceMap,
        mapStore.mapFilterState,
        mapStore.mapFilterOptions,
    ]);

    const saveMapLayers = useCallback(() => {
        if (rollOutPersistWorkspaceMapFiltersEnabled && workspaceMap) {
            const layersForWorkspaceDto = convertMapLayerIdsToWorkspaceMapLayers(
                mapStore.visibleLayersIds
            );
            workspaceMap.updateMapLayers(layersForWorkspaceDto);
        }
    }, [rollOutPersistWorkspaceMapFiltersEnabled, workspaceMap, mapStore.visibleLayersIds]);

    const isAlternativeBasemap = shouldLoadAlternativeBasemap();

    useEffect(
        function initializeMapFiltersAndLayers() {
            if (
                !mapStore.isBootstrapQuerySet &&
                mapBootstrapQuery.data &&
                !mapBootstrapQuery.isPreviousData
            ) {
                const {
                    cfsPriorityLabelsById,
                    virtualPatrolLabel,
                    priorityOptions,
                    dispatchAreaOptions,
                    unitStatusOptions,
                    agencyTypeOptions,
                } = mapBootstrapQuery.data;

                const primarySubdivisionNames: PrimarySubdivisionNames = {
                    primaryPoliceSubdivisionName,
                    primaryFireSubdivisionName,
                    primaryEmsSubdivisionName,
                };
                const subdivisionOptions = getPrimarySubdivisionOptions({
                    subdivisionOptionsBootstrapView: multiAgencySubdivisions
                        ? subdivisionOptionsBootstrapResponse
                              ?.agencyIdToSubdivisionOptionsBootstrapView?.[agencyId]
                        : subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView,
                    agencyTypeGlobalAttrId: agencyTypeGlobalAttrId as AgencyTypeGlobal,
                    primarySubdivisionNames,
                });
                const primarySubdivisionName =
                    primarySubdivisionNames[
                        getPrimarySubdivisionNameKeyByAgencyType(
                            agencyTypeGlobalAttrId as AgencyTypeGlobal
                        )
                    ];
                const agencyTypeIdToDisplay =
                    agencyTypeIdToDisplayByAgencyTypeGlobal[
                        agencyTypeGlobalAttrId as AgencyTypeGlobal
                    ]?.capitalize;

                if (!rollOutPersistWorkspaceMapFiltersEnabled) {
                    const mapPriorityOptions = Object.keys(cfsPriorityLabelsById)
                        ?.map((key) => ({
                            value: cfsPriorityLabelsById[key],
                            label: cfsPriorityLabelsById[key],
                        }))
                        .sort((optionLeft, optionRight) =>
                            alphabeticalStringComparator(optionLeft.label, optionRight.label)
                        );
                    const mapDispatchAreaOptions =
                        manageDispatchAreasView.allDispatchAreaObjects?.map(({ name }) => ({
                            value: name,
                            label: name,
                        }));
                    const mapSubdivisionOptions = subdivisionOptions.map(({ label }) => ({
                        value: label,
                        label,
                    }));

                    const mapAgencyTypeOptions = isFirstResponder
                        ? [
                              ...(userAgencyTypeGlobalAttrId ===
                              globalAttributes.agencyTypeGlobal.POLICE
                                  ? [
                                        {
                                            value: String(globalAttributes.agencyTypeGlobal.POLICE),
                                            label: agencyTypeIdToDisplayByAgencyTypeGlobal[
                                                globalAttributes.agencyTypeGlobal.POLICE
                                            ].capitalize,
                                        },
                                    ]
                                  : []),
                              {
                                  value: String(globalAttributes.agencyTypeGlobal.FIRE),
                                  label: agencyTypeIdToDisplayByAgencyTypeGlobal[
                                      globalAttributes.agencyTypeGlobal.FIRE
                                  ].capitalize,
                              },
                              {
                                  value: String(globalAttributes.agencyTypeGlobal.EMS),
                                  label: agencyTypeIdToDisplayByAgencyTypeGlobal[
                                      globalAttributes.agencyTypeGlobal.EMS
                                  ].capitalize,
                              },
                          ]
                        : undefined;

                    const mapSummaryCategoryOptions = virtualPatrolLabel
                        ? SUMMARY_FILTER_CATEGORIES?.map((option) =>
                              option.value === 'UNIT_VIRTUAL_PATROL'
                                  ? {
                                        ...option,
                                        label: virtualPatrolLabel.toUpperCase(),
                                    }
                                  : option
                          )
                        : SUMMARY_FILTER_CATEGORIES;

                    mapStore.setDeprecatedMapFilterOptions({
                        ...(mapAgencyTypeOptions
                            ? { agencyTypeOptions: mapAgencyTypeOptions }
                            : {}),
                        priorityOptions: mapPriorityOptions,
                        summaryCategoryOptions: mapSummaryCategoryOptions,
                        dispatchAreaOptions: mapDispatchAreaOptions,
                        subdivisionOptions: mapSubdivisionOptions,
                        primarySubdivisionName,
                        agencyTypeIdToDisplay,
                    });

                    const mapFilters: DeprecatedEsriMapFilterState | null = storageHelpers.getItem(
                        `${userId}:${
                            isFirstResponder
                                ? FR_MAP_FILTERS_LOCAL_STORAGE_KEY
                                : CAD_MAP_FILTERS_LOCAL_STORAGE_KEY
                        }`
                    );
                    if (mapFilters) {
                        setDeprecatedMapFilterState(mapFilters, false);
                    }
                    const mapLayers: LayerId[] | null = storageHelpers.getItem(
                        `${userId}:${MAP_LAYERS_LOCAL_STORAGE_KEY}`
                    );
                    if (mapLayers) {
                        setLayerIds(mapLayers, false);
                    }
                    return;
                }
                const mapFilterOptions = {
                    agencyProfileOptions,
                    agencyTypeOptions,
                    priorityOptions,
                    unitStatusOptions,
                    dispatchAreaOptions,
                    subdivisionOptions,
                    primarySubdivisionName,
                    agencyTypeIdToDisplay,
                };
                mapStore.setMapFilterOptions(mapFilterOptions);
                mapStore.setMapFilterState(
                    initializeMapFilterStateFromWorkspaceMapDto(workspaceMap?.getDto())
                );
                mapStore.setVisibleLayersIds(
                    initializeMapLayerIdsFromWorkspaceMapDto(workspaceMap?.getDto())
                );
            }
        },
        [
            manageDispatchAreasView.allDispatchAreaObjects,
            subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView,
            agencyProfileOptions,
            agencyTypeGlobalAttrId,
            primaryPoliceSubdivisionName,
            primaryFireSubdivisionName,
            primaryEmsSubdivisionName,
            mapStore,
            mapStore.isBootstrapQuerySet,
            mapBootstrapQuery.data,
            mapBootstrapQuery.isPreviousData,
            multiAgencySubdivisions,
            subdivisionOptionsBootstrapResponse?.agencyIdToSubdivisionOptionsBootstrapView,
            agencyId,
            userId,
            userAgencyTypeGlobalAttrId,
            isFirstResponder,
            setDeprecatedMapFilterState,
            setFilterState,
            setLayerIds,
            rollOutPersistWorkspaceMapFiltersEnabled,
            workspaceStore,
            workspaceMap,
        ]
    );

    useEffect(
        function bootstrapMap() {
            if (
                !mapStore.isBootstrapQuerySet &&
                mapBootstrapQuery.data &&
                !mapBootstrapQuery.isPreviousData
            ) {
                const {
                    mapCenter,
                    mapViewportEvents,
                    mapLedgerEvents,
                    mapViewportUnits,
                    mapLedgerUnits,
                    mapViewportHospitals,
                    mapLedgerHospitals,
                    stations,
                } = mapBootstrapQuery.data;
                mapStore.setMapCenter(savedMapCenter ?? mapCenter);
                if (savedMapZoomLevel) {
                    mapStore.setMapZoomLevel(savedMapZoomLevel);
                }
                mapStore.setStations(stations);
                mapStore.setMapViewportEvents(mapViewportEvents);
                mapStore.setMapLedgerEvents(mapLedgerEvents);
                mapStore.setMapViewportUnits(mapViewportUnits);
                mapStore.setMapLedgerUnits(mapLedgerUnits);
                mapStore.setMapViewportHospitals(mapViewportHospitals);
                mapStore.setMapLedgerHospitals(mapLedgerHospitals);
                mapStore.setIsBootstrapQuerySet(true);
            }
        },
        [
            mapStore,
            mapStore.isBootstrapQuerySet,
            mapBootstrapQuery.data,
            mapBootstrapQuery.isPreviousData,
            savedMapCenter,
            savedMapZoomLevel,
        ]
    );

    useEffect(
        function resetMapOnWorkspaceSelect() {
            resetWorkspaceMapView(workspaceMapDto);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [resetWorkspaceMapView, mapWorkspaceWindow?.id]
    );

    useInaccurateLocationCheckInterval();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onMapChange = useCallback(
        throttle(
            (params: MapOnChangeParams) => {
                mapStore.setMarginBounds(params.marginBounds);
                if (mapStore.selectedUnitId) {
                    const selectedUnitLocation = getMapViewportUnitLocation(
                        mapStore.getMapViewportUnit(mapStore.selectedUnitId),
                        showUnitLocationWhenGpsIsNotAvailable
                    );
                    mapStore.setIsFollowModeEnabled(
                        mapStore.isFollowModeEnabled &&
                            (!mapStore.selectedUnitId ||
                                areLatLngsEqual(params.center, selectedUnitLocation))
                    );
                }
            },
            MAP_ON_CHANGE_THROTTLE_THRESHOLD,
            { trailing: true }
        ),
        [mapStore]
    );
    const moveMap = useCallback(() => {
        //
    }, []);

    const getPhoneNumberIds = usePhoneNumberIdsForEvent();

    const selectEvent = useCallback(
        (eventId: number, shouldResetZoomLevel: boolean = false) => {
            const mapLedgerEvent = mapStore.getMapLedgerEvent(eventId);
            const eventLocation = getEventLocation(mapLedgerEvent);

            mapStore.setRelatedAmlRapidSOSCallsEntities({
                phoneNumberIds: getPhoneNumberIds(eventId),
                amlCallsList: phoneCallsStore.amlCallsList,
                activePhoneCallsList: phoneCallsStore.activePhoneCallsList,
            });

            mapStore.refreshEntitiesAndPolylines({
                shouldMoveCenter: true,
                shouldRefreshLedgerEntity: true,
                entityType: EsriMapEntityTypeEnum.EVENT,
                data: {
                    mapCenterLatitude: eventLocation?.latitude,
                    mapCenterLongitude: eventLocation?.longitude,
                    entityData: eventId,
                },
                shouldResetZoomLevel,
            });
        },
        [mapStore, phoneCallsStore, getPhoneNumberIds]
    );
    const openEventContextMenu = useCallback(
        (eventId: number) => {
            mapStore.openEntityContextMenu(EsriMapContextMenuEntityTypeEnum.EVENT, eventId);
        },
        [mapStore]
    );
    const selectUnit = useCallback(
        (unitId: number, shouldResetZoomLevel: boolean = false) => {
            const mapViewportUnit = mapStore.getMapViewportUnit(unitId);
            const unitLocation = getMapViewportUnitLocation(
                mapViewportUnit,
                showUnitLocationWhenGpsIsNotAvailable
            );

            if (mapViewportUnit?.assignedEventId) {
                mapStore.setRelatedAmlRapidSOSCallsEntities({
                    phoneNumberIds: getPhoneNumberIds(mapViewportUnit.assignedEventId),
                    amlCallsList: phoneCallsStore.amlCallsList,
                    activePhoneCallsList: phoneCallsStore.activePhoneCallsList,
                });
            }

            mapStore.refreshEntitiesAndPolylines({
                shouldRefreshLedgerEntity: true,
                shouldMoveCenter: true,
                entityType: EsriMapEntityTypeEnum.UNIT,
                data: {
                    entityData: unitId,
                    mapCenterLatitude: unitLocation?.latitude,
                    mapCenterLongitude: unitLocation?.longitude,
                },
                shouldResetZoomLevel,
            });
        },
        [mapStore, phoneCallsStore, showUnitLocationWhenGpsIsNotAvailable, getPhoneNumberIds]
    );

    const setAssignedUnitForFollowMode = useCallback(() => {
        if (unitAssignment?.unitId && mapStore.selectedUnitId === undefined) {
            selectUnit(unitAssignment?.unitId);
        }
    }, [mapStore.selectedUnitId, selectUnit, unitAssignment?.unitId]);

    const openUnitContextMenu = useCallback(
        (unitId: number) => {
            mapStore.openEntityContextMenu(EsriMapContextMenuEntityTypeEnum.UNIT, unitId);
        },
        [mapStore]
    );
    const selectUnitUser = useCallback(
        (unitUserId: number, shouldResetZoomLevel: boolean = false) => {
            const unitId = mapStore.getUnitIdByUserId(unitUserId);
            if (unitId) {
                const mapViewportUnit = mapStore.getMapViewportUnit(unitId);
                const unitUserLocation = mapViewportUnit?.unitUserLocations?.find(
                    (unitLocation) => unitLocation.entityId === unitUserId
                );

                const crosshairLocation = getUnitUserLocation(unitUserLocation);

                if (mapViewportUnit?.assignedEventId) {
                    mapStore.setRelatedAmlRapidSOSCallsEntities({
                        phoneNumberIds: getPhoneNumberIds(mapViewportUnit.assignedEventId),
                        amlCallsList: phoneCallsStore.amlCallsList,
                        activePhoneCallsList: phoneCallsStore.activePhoneCallsList,
                    });
                }

                mapStore.refreshEntitiesAndPolylines({
                    shouldRefreshLedgerEntity: true,
                    shouldMoveCenter: true,
                    entityType: EsriMapEntityTypeEnum.USER,
                    data: {
                        entityData: unitUserId,
                        mapCenterLatitude: crosshairLocation?.latitude,
                        mapCenterLongitude: crosshairLocation?.longitude,
                    },
                    shouldResetZoomLevel,
                });
            }
        },
        [mapStore, phoneCallsStore, getPhoneNumberIds]
    );
    const selectCluster = useCallback(
        (clusterId: number) => {
            mapStore.refreshEntitiesAndPolylines({
                shouldRefreshLedgerEntity: true,
                entityType: EsriMapEntityTypeEnum.CLUSTER,
                data: {
                    entityData: clusterId,
                },
            });
        },
        [mapStore]
    );
    const selectStation = useCallback(
        (stationId: number) => {
            const selectedStation = mapStore.getStation(stationId);

            mapStore.refreshEntitiesAndPolylines({
                shouldRefreshLedgerEntity: true,
                shouldMoveCenter: true,
                entityType: EsriMapEntityTypeEnum.STATION,
                data: {
                    entityData: stationId,
                    mapCenterLatitude: selectedStation?.location?.latitude,
                    mapCenterLongitude: selectedStation?.location?.longitude,
                },
            });
        },
        [mapStore]
    );
    const selectHospital = useCallback(
        (hospitalId: number) => {
            const selectedHospital = mapStore.getMapViewportHospital(hospitalId);

            mapStore.refreshEntitiesAndPolylines({
                shouldRefreshLedgerEntity: true,
                shouldMoveCenter: true,
                entityType: EsriMapEntityTypeEnum.HOSPITAL,
                data: {
                    entityData: hospitalId,
                    mapCenterLatitude: selectedHospital?.geoLocation?.latitude,
                    mapCenterLongitude: selectedHospital?.geoLocation?.longitude,
                },
            });
        },
        [mapStore]
    );
    const selectGeoPointLocation = useCallback((_geoPointLocationId: number) => {
        // TODO: Map - change according needs
        // mapStore.refreshEntitiesAndPolylines({
        //     shouldRefreshLedgerEntity: true,
        //     entityType: EsriMapEntityTypeEnum.GEO_POINT_LOCATION,
        //     data: {
        //         entityData: geoPointLocationId,
        //     },
        // });
    }, []);

    const pinGeoPointOnMap = useCallback(
        (latitude: number, longitude: number) => {
            if (!createEventAtLocationPin) return;

            mapStore.showLocationOnTheMap({ latitude, longitude });
        },
        [mapStore, createEventAtLocationPin]
    );

    const selectAmlLocation = useCallback(
        (amlCallId: number) => {
            const amlCall = phoneCallsStore.getAmlCallById(amlCallId);

            if (!amlCall) return;

            mapStore.refreshEntitiesAndPolylines({
                shouldRefreshLedgerEntity: true,
                shouldMoveCenter: true,
                entityType: EsriMapEntityTypeEnum.AML_CALL,
                data: {
                    entityData: amlCall.phoneNumberId,
                    mapCenterLatitude: amlCall?.normalizedPhoneCallDisplay?.latFloat,
                    mapCenterLongitude: amlCall?.normalizedPhoneCallDisplay?.lngFloat,
                },
            });
        },
        [mapStore, phoneCallsStore]
    );
    const selectPhoneCall = useCallback(
        (phoneCallId?: number) => {
            if (!phoneCallId) return;

            const phoneCall = phoneCallsStore.getPhoneCall(phoneCallId as number) as PhoneCallView;

            mapStore.refreshEntitiesAndPolylines({
                shouldRefreshLedgerEntity: true,
                shouldMoveCenter: true,
                entityType: EsriMapEntityTypeEnum.PHONE_CALL,
                data: {
                    entityData: phoneCallId,
                    mapCenterLatitude: phoneCall?.normalizedPhoneCallDisplay?.latFloat,
                    mapCenterLongitude: phoneCall?.normalizedPhoneCallDisplay?.lngFloat,
                },
            });
        },
        [mapStore, phoneCallsStore]
    );

    const phoneCallGeoPoint = useMemo(() => {
        if (!mapStore.phoneCallEntity) return;

        return convertPhoneCallToGeoPointEntity(mapStore.phoneCallEntity);
    }, [mapStore.phoneCallEntity]);

    const areRapidSOSAniAliLayersVisible = useMemo(() => {
        return mapStore.selectedEventId
            ? !!mapStore.getMapViewportEvent(mapStore.selectedEventId)?.visible
            : !!(
                  mapStore.phoneCallEntity ||
                  mapStore.amlCallEntity ||
                  mapStore.selectedPhoneCallId ||
                  mapStore.selectedAmlPhoneNumberId
              );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        mapStore,
        mapStore.selectedEventId,
        mapStore.deprecatedMapFilterState,
        mapStore.phoneCallEntity,
        mapStore.amlCallEntity,
        mapStore.selectedPhoneCallId,
        mapStore.selectedAmlPhoneNumberId,
    ]);

    const onMapLoadCompleted = useCallback(
        (webMap?: WebMap) => {
            mapStore.setIsMapLoaded(true);

            const usersUnitId = unitAssignment?.unitId;

            if (!isFirstResponder && !isElectron()) {
                while (!mapStore.isDelayedActionsQueueEmpty) {
                    const action = mapStore.getNextDelayedAction();

                    if (action) {
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                        const delayedActionTimerId = setTimeout(() => {
                            esriMapHandleViewLocationOnMapAction(action);
                        }, 0);

                        esriMapDelayedActionsTimeoutIds.push(delayedActionTimerId);
                    }
                }
            }

            if (
                isFirstResponder &&
                unitAssignment &&
                mapStore.shouldMoveMapCenterToLoggedInUnit &&
                usersUnitId
            ) {
                const mapViewportUnit = mapStore.getMapViewportUnit(usersUnitId);
                const unitLocation = getMapViewportUnitLocation(
                    mapViewportUnit,
                    showUnitLocationWhenGpsIsNotAvailable
                );
                const mapLoadDelayMs = mapLastInactiveTimestamp
                    ? ESRI_MAP_FULL_LOAD_DELAY_REOPEN_MS
                    : ESRI_MAP_FULL_LOAD_DELAY_INITIAL_MS;

                // eslint-disable-next-line react-hooks/exhaustive-deps
                esriMapLoadTimeoutId = setTimeout(() => {
                    if (unitLocation && !mapStore.followingUnitId) {
                        moveLayerOnTop(webMap, FEATURE_LAYERS_PROPERTIES.UNITS.layerKey);
                        selectUnit(usersUnitId);

                        mapStore.setFollowingUnitId(usersUnitId);
                        mapStore.setIsFollowModeEnabled(true);
                    }
                }, mapLoadDelayMs);
            }
        },
        [
            isFirstResponder,
            unitAssignment,
            mapStore,
            showUnitLocationWhenGpsIsNotAvailable,
            selectUnit,
            mapStore.followingUnitId,
            mapStore.shouldMoveMapCenterToLoggedInUnit,
            mapStore.isDelayedActionsQueueEmpty,
            esriMapHandleViewLocationOnMapAction,
        ]
    );

    const onMapUnmount = useCallback(() => {
        clearTimeout(esriMapLoadTimeoutId);
        mapStore.setIsMapLoaded(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cleanupDelayedActions = useCallback(() => {
        if (!isFirstResponder && !isElectron()) {
            mapStore.resetDelayedActionQueue();
            esriMapDelayedActionsTimeoutIds?.map((id) => clearTimeout(id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { isFetching, isError } = mapStore.mapBootstrapState ?? {};

    if (isError === true) {
        return (
            <InlineBanner
                status="error"
                title={errorOccurredWhile('loading the map').split('.')[0]}
                description={errorOccurredWhile('loading the map').split('.')[1]}
                style={{
                    position: 'absolute',
                    marginTop: '40px',
                    zIndex: 1,
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                }}
                data-testid="MAP_LOADING_ERROR_INFO_BANNER"
            />
        );
    }

    if (isFetching === undefined || isFetching === true) {
        return (
            <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                <Spinner size="lg" />
            </Flex>
        );
    }

    return (
        <Box w="100%" h="100%" position="relative" data-testid="ESRI_MAP_WINDOW">
            <Flex height="100%">
                <MapTopMenuBar
                    selectUnit={selectUnit}
                    selectEvent={selectEvent}
                    selectUnitUser={selectUnitUser}
                />
                <MapLedger />
                <EsriMap
                    amlCall={mapStore.amlCallEntity}
                    areRapidSOSAniAliLayersVisible={areRapidSOSAniAliLayersVisible}
                    centerForUpdate={mapStore.mapCenterForUpdate}
                    cleanupDelayedActions={cleanupDelayedActions}
                    closeEntityContextMenu={mapStore.closeEntityContextMenu}
                    clusterRadius={mapClusterRadiusInPixels}
                    contextMenuEntityId={mapStore.contextMenuEntityId}
                    createEventAtLocationPin={createEventAtLocationPin}
                    defaultCenter={mapStore.mapCenter}
                    defaultZoomLevel={mapStore.mapZoomLevel}
                    deprecatedMapFilterOptions={mapStore.deprecatedMapFilterOptions}
                    deprecatedMapFilterState={mapStore.deprecatedMapFilterState}
                    dispatchFromMap={dispatchFromMap}
                    enableUnitClusteringOnMap={enableUnitClusteringOnMap}
                    entityViewZoomLevel={ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL}
                    esriFeatureReductionClusteringEnabled={esriFeatureReductionClusteringEnabled}
                    filterOptions={mapStore.mapFilterOptions}
                    filterState={mapStore.mapFilterState}
                    initialGeoPoint={mapStore.initialGeoPoint}
                    isAlternativeBasemap={isAlternativeBasemap}
                    isFirstResponder={isFirstResponder}
                    isFollowModeEnabled={mapStore.isFollowModeEnabled}
                    isMapInactive={isMapInactive}
                    isMapLedgerOpen={mapStore.isMapLedgerOpen}
                    mapMode={mapStore.mapMode}
                    mapViewportEvents={mapStore.mapViewportEvents}
                    mapViewportGeoPointLocations={
                        mapStore.locationSearchSelectedGeoPointLocation
                            ? [mapStore.locationSearchSelectedGeoPointLocation]
                            : []
                    }
                    mapViewportHospitals={hospitalsEnabled ? mapStore.mapViewportHospitals : []}
                    mapViewportPolylines={mapStore.mapViewportPolylines}
                    mapViewportUnits={mapStore.mapViewportUnits}
                    mapViewRef={mapViewRef}
                    moveMap={moveMap}
                    nearbyGeoCautions={mapStore.nearbyGeoCautions}
                    onMapChange={onMapChange}
                    onMapLoadCompleted={onMapLoadCompleted}
                    onMapUnmount={onMapUnmount}
                    openEventContextMenu={openEventContextMenu}
                    openUnitContextMenu={openUnitContextMenu}
                    phoneCallGeoPointEntity={phoneCallGeoPoint}
                    pinGeoPointOnMap={pinGeoPointOnMap}
                    renderEventContextMenuComponent={(eventId: number) => (
                        <EventContextMenu eventId={eventId} isContextMenuOpenFromAMap />
                    )}
                    renderUnitContextMenuComponent={(unitId: number) => (
                        <UnitContextMenu unitId={unitId} isContextMenuOpenFromAMap />
                    )}
                    rollOutPersistWorkspaceMapFiltersEnabled={
                        rollOutPersistWorkspaceMapFiltersEnabled
                    }
                    saveFilters={saveMapFilters}
                    saveLayers={saveMapLayers}
                    selectAmlLocation={selectAmlLocation}
                    selectCluster={selectCluster}
                    selectEvent={selectEvent}
                    selectGeoPointLocation={selectGeoPointLocation}
                    selectHospital={selectHospital}
                    selectPhoneCall={selectPhoneCall}
                    selectStation={selectStation}
                    selectUnit={selectUnit}
                    selectUnitUser={selectUnitUser}
                    selectedAmlPhoneNumberId={mapStore.selectedAmlPhoneNumberId}
                    selectedClusterId={mapStore.selectedClusterId}
                    selectedContextMenuEntityType={mapStore.selectedContextMenuEntityType}
                    selectedEventId={mapStore.selectedEventId}
                    selectedEventLocation={selectedEventLocation}
                    selectedGeoPointLocationId={
                        mapStore.locationSearchSelectedGeoPointLocation?.entityId
                    }
                    selectedHospitalId={mapStore.selectedHospitalId}
                    selectedMapLedgerEntityType={mapStore.selectedMapLedgerEntityType}
                    selectedNearbyGeoCautionCoordinates={
                        mapStore.selectedNearbyGeoCautionCoordinates
                    }
                    selectedPhoneCallId={mapStore.selectedPhoneCallId}
                    selectedStationId={mapStore.selectedStationId}
                    selectedUnitId={mapStore.selectedUnitId}
                    selectedUnitLocation={selectedUnitLocation}
                    selectedUnitUserId={mapStore.selectedUnitUserId}
                    setAssignedUnitForFollowMode={setAssignedUnitForFollowMode}
                    setDeprecatedMapFilterState={setDeprecatedMapFilterState}
                    setFilterState={setFilterState}
                    setIsFollowModeEnabled={mapStore.setIsFollowModeEnabled}
                    setMapLastInactiveTimestamp={setMapLastInactiveTimestamp}
                    setMapMode={mapStore.setMapMode}
                    setVisibleLayersIds={setLayerIds}
                    setZoomForUpdate={mapStore.setMapZoomForUpdate}
                    shouldResetMapOnInitialLoad={shouldResetMapOnInitialLoad}
                    showMobileDevicesOnMap={showMobileDevicesOnMap}
                    showUnitLocationWhenGpsIsNotAvailable={showUnitLocationWhenGpsIsNotAvailable}
                    stations={mapStore.stations}
                    theme={theme}
                    visibleLayersIds={mapStore.visibleLayersIds}
                    webmapPortalId={esriWebmapPortalId}
                    zoomForUpdate={mapStore.mapZoomForUpdate}
                />
            </Flex>
        </Box>
    );
});
